import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Función simplificada para obtener categorías
  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Simulamos la obtención de categorías para demostración
      // En un entorno real, esto se conectaría a tu backend
      
      // Creamos algunas categorías de ejemplo
      const mockCategories = [
        {
          id: '1',
          name: 'Salud Mental',
          description: 'Categoría para productos relacionados con la salud mental',
          status: 'active'
        },
        {
          id: '2',
          name: 'Sueño y Relajación',
          description: 'Productos para mejorar la calidad del sueño y la relajación',
          status: 'active'
        },
        {
          id: '3',
          name: 'Apoyo Anti-Estrés y Estado de Ánimo',
          description: 'Productos para el manejo del estrés y equilibrio del estado de ánimo',
          status: 'active'
        },
        {
          id: '4',
          name: 'Longevidad y Salud Anti Envejecimiento',
          description: 'Productos para promover la longevidad y combatir el envejecimiento',
          status: 'active'
        },
        {
          id: '5',
          name: 'Salud Gastrointestinal y Pérdida de Peso',
          description: 'Productos para la salud digestiva y control de peso',
          status: 'active'
        },
        {
          id: '6',
          name: 'Deporte y Modo de Vida Activo',
          description: 'Productos para deportistas y personas con estilo de vida activo',
          status: 'active'
        },
        {
          id: '7',
          name: 'Apoyo al Sistema Cognitivo',
          description: 'Productos para mejorar la función cognitiva y la memoria',
          status: 'active'
        },
        {
          id: '8',
          name: 'Salud Metabólica y Apoyo Azúcar en la Sangre',
          description: 'Productos para regular el metabolismo y los niveles de azúcar',
          status: 'active'
        },
        {
          id: '9',
          name: 'Apoyo al Sistema Inmune',
          description: 'Productos para fortalecer el sistema inmunológico',
          status: 'active'
        },
        {
          id: '10',
          name: 'Piel Saludable',
          description: 'Productos para el cuidado y la salud de la piel',
          status: 'active'
        }
      ];
      
      // Sin retrasos artificiales para mejorar la velocidad
      
      setCategories(mockCategories);
      setError(null);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
      setError('No se pudieron cargar las categorías. Verifica tu conexión e inténtalo de nuevo.');
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);
  
  // Función simplificada para eliminar una categoría
  const deleteCategory = async (categoryId) => {
    try {
      // Simulamos la eliminación de la categoría
      // En un entorno real, esto se conectaría a tu backend
      
      // Sin retrasos artificiales para mejorar la velocidad
      
      // Actualizar la lista de categorías (eliminación local)
      setCategories(categories.filter(c => c.id !== categoryId));
      toast.success('Categoría eliminada correctamente');
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error al eliminar categoría:', err);
      toast.error('Error al eliminar categoría');
    }
  };
  
  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Categorías</h1>
        <Link to="/admin/categories/new" className="btn btn-primary">
          <FaPlus className="me-2" />
          Nueva Categoría
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <p>Cargando...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        No hay categorías registradas
                      </td>
                    </tr>
                  ) : (
                    categories.map(category => (
                      <tr key={category.id}>
                        <td>{category.name}</td>
                        <td>{category.description}</td>
                        <td>
                          <span className={`badge bg-${category.status === 'active' ? 'success' : 'secondary'}`}>
                            {category.status === 'active' ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            <Link to={`/admin/categories/edit/${category.id}`} className="btn btn-sm btn-outline-primary">
                              <FaEdit />
                            </Link>
                            <button 
                              className="btn btn-sm btn-outline-danger" 
                              onClick={() => setConfirmDelete(category.id)}
                              title="Eliminar categoría"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para eliminar - simplificado sin transiciones */}
      {confirmDelete && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar eliminación</h5>
                <button type="button" className="btn-close" onClick={() => setConfirmDelete(null)}></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro de que deseas eliminar esta categoría? Esta acción no se puede deshacer.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancelar</button>
                <button type="button" className="btn btn-danger" onClick={() => deleteCategory(confirmDelete)}>Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
